// content-type:application/json;charset=UTF-8
// accept:application/json
// x-client-id:12345
// x-api-key:<REACT_APP_API_KEY 환경변수>
// x-emp-token:<로그인 후 localStorage.accessToken>
// x-country-code:KR
// x-language-code:ko-KR
// x-message-id:5wbwf6yUxVBcr48AMbz9990
// x-service-code:CHN000037
// x-service-phase:DEV
// x-api-version:3.2
// x-thinq-client-type:WEB
// x-user-no:000037KR2602260511999
// x-thinq-app-version:0.1
// x-thinq-app-ver:3.2
// x-thinq-app-level:DEV

import axios from "axios";
import LocalStorageKey from "../../../common/enums/LocalStorageKey";
import StaticApiHeader from "./StaticApiHeader";
import EncryptUtil from "../../../common/utils/EncryptUtil";

// 환경별로 달라지는 값은 빌드 시 주입한다. 로컬은 .env, 배포는 setup.sh 참조.
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? "";
const API_KEY = process.env.REACT_APP_API_KEY ?? "";

if (process.env.NODE_ENV !== "production") {
    if (!API_BASE_URL) console.error("[CommonApi] REACT_APP_API_BASE_URL 미설정. .env 를 확인하세요.");
    if (!API_KEY) console.error("[CommonApi] REACT_APP_API_KEY 미설정. .env 를 확인하세요.");
}

let axiosChain: Promise<unknown> = Promise.resolve();

axios.interceptors.request.use(async (config) => {
    const prev = axiosChain;
    let release: () => void = () => {};
    axiosChain = new Promise<void>((resolve) => {
        release = resolve;
    });
    (config as any).__releaseChain = release;
    await prev;
    return config;
});

axios.interceptors.response.use(
    (response) => {
        (response.config as any)?.__releaseChain?.();
        return response;
    },
    (error) => {
        error?.config?.__releaseChain?.();
        return Promise.reject(error);
    }
);

class CommonApi {
    selectedCountry = localStorage.getItem(LocalStorageKey.server) || "TH";
    selectedLanguage = localStorage.getItem(LocalStorageKey.language) || "en";

    BASE_SERVICE_URL = API_BASE_URL;
    CONTENT_TYPE = StaticApiHeader.CONTENT_TYPE;
    ACCEPT = StaticApiHeader.ACCEPT;
    X_CLIENT_ID = StaticApiHeader.X_CLIENT_ID;
    X_API_KEY = API_KEY;
    get X_EMP_TOKEN() {
        return localStorage.getItem(LocalStorageKey.accessToken);
    }
    get X_USER_NO() {
        return localStorage.getItem(LocalStorageKey.userNo);
    }
    X_COUNTRY_CODE = this.selectedCountry;
    X_LANGUAGE_CODE = `${this.selectedLanguage}`;
    X_SERVICE_CODE = StaticApiHeader.PROD_X_SERVICE_CODE;
    X_SERVICE_PHASE = StaticApiHeader.QA_X_SERVICE_PHASE;
    // X_API_VERSION = StaticApiHeader.X_API_VERSION;
    X_THINQ_CLIENT_TYPE = StaticApiHeader.X_THINQ_CLIENT_TYPE;
    // X_THINQ_CLIENT_VERSION = StaticApiHeader.X_THINQ_CLIENT_VERSION;
    // X_THINQ_APP_VER = StaticApiHeader.X_THINQ_APP_VER;
    X_THINQ_APP_LEVEL = StaticApiHeader.QA_X_THINQ_APP_LEVEL;

    makeBaseHeader() {
        return {
            "Content-Type": this.CONTENT_TYPE,
            Accept: this.ACCEPT,
            "X-Client-Id": this.X_CLIENT_ID,
            "X-Api-Key": this.X_API_KEY,
            "X-Emp-Token": this.X_EMP_TOKEN,
            "X-User-No": this.X_USER_NO,
            "X-Country-Code": this.X_COUNTRY_CODE,
            "X-Language-Code": this.X_LANGUAGE_CODE,
            "X-Message-Id": EncryptUtil.generateSecureRandomString(24),
            "X-Service-Code": this.X_SERVICE_CODE,
            "X-Service-Phase": this.X_SERVICE_PHASE,
            // "X-Api-Version": "",
            "X-Thinq-Client-Type": this.X_THINQ_CLIENT_TYPE,
            // "X-Thinq-Client-Version": this.X_THINQ_CLIENT_VERSION,
            // "X-Thinq-App-Ver": this.X_THINQ_APP_VER,
            "X-Thinq-App-Level": this.X_THINQ_APP_LEVEL,
        };
    }

    makeMultipartHeader() {
        return {
            "Content-Type": "multipart/form-data",
            Accept: this.ACCEPT,
            "X-Client-Id": this.X_CLIENT_ID,
            "X-Api-Key": this.X_API_KEY,
            "X-Emp-Token": this.X_EMP_TOKEN,
            "X-User-No": this.X_USER_NO,
            "X-Country-Code": this.X_COUNTRY_CODE,
            "X-Language-Code": this.X_LANGUAGE_CODE,
            "X-Message-Id": EncryptUtil.generateSecureRandomString(24),
            "X-Service-Code": this.X_SERVICE_CODE,
            "X-Service-Phase": this.X_SERVICE_PHASE,
            // "X-Api-Version": "",
            "X-Thinq-Client-Type": this.X_THINQ_CLIENT_TYPE,
            // "X-Thinq-Client-Version": this.X_THINQ_CLIENT_VERSION,
            // "X-Thinq-App-Ver": this.X_THINQ_APP_VER,
            "X-Thinq-App-Level": this.X_THINQ_APP_LEVEL,
        };
    }

    makeBaseHeaderWithClientVersion({ xApiVersion = 3.1 }) {
        return {
            "Content-Type": this.CONTENT_TYPE,
            Accept: this.ACCEPT,
            "X-Client-Id": this.X_CLIENT_ID,
            "X-Api-Key": this.X_API_KEY,
            "X-Emp-Token": this.X_EMP_TOKEN,
            "X-User-No": this.X_USER_NO,
            "X-Country-Code": this.X_COUNTRY_CODE,
            "X-Language-Code": this.X_LANGUAGE_CODE,
            "X-Message-Id": EncryptUtil.generateSecureRandomString(24),
            "X-Service-Code": this.X_SERVICE_CODE,
            "X-Service-Phase": this.X_SERVICE_PHASE,
            // "X-Api-Version": "",
            "X-Thinq-Client-Type": this.X_THINQ_CLIENT_TYPE,
            "x-api-version": xApiVersion,
            // "X-Thinq-App-Ver": this.X_THINQ_APP_VER,
            "X-Thinq-App-Level": this.X_THINQ_APP_LEVEL,
        };
    }
}

export default CommonApi;
